import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  EventNote as FristIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  Done as DoneIcon,
  OpenInNew as OpenIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { fristenApi } from '../services/api';
import type { Frist, FristStatistik } from '../types/api';
import AdresseDetailDialog from '../components/AdresseDetailDialog';

type ViewMode = 'liste' | 'kunde' | 'kategorie';
type FilterMode = 'alle' | 'ueberfaellig' | 'heute' | 'dieseWoche' | 'diesesMonat';

const FristenDashboard: React.FC = () => {
  const [fristen, setFristen] = useState<Frist[]>([]);
  const [statistik, setStatistik] = useState<FristStatistik | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('liste');
  const [filterMode, setFilterMode] = useState<FilterMode>('alle');
  const [kategorieFilter, setKategorieFilter] = useState<string>('alle');
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAdresseId, setSelectedAdresseId] = useState<number | null>(null);
  const [erledigtDialogOpen, setErledigtDialogOpen] = useState(false);
  const [selectedFrist, setSelectedFrist] = useState<Frist | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [fristenRes, statistikRes, kategorienRes] = await Promise.all([
        fristenApi.getAll(),
        fristenApi.getStatistik(),
        fristenApi.getKategorien(),
      ]);

      setFristen(fristenRes.data);
      setStatistik(statistikRes.data);
      setKategorien(kategorienRes.data);
    } catch (err: any) {
      console.error('Fehler beim Laden:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Fristen');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsErledigt = async () => {
    if (!selectedFrist) return;

    try {
      await fristenApi.markAsErledigt(selectedFrist.fristID);
      setErledigtDialogOpen(false);
      setSelectedFrist(null);
      loadData();
    } catch (err: any) {
      console.error('Fehler beim Erledigen:', err);
    }
  };

  const filteredFristen = useMemo(() => {
    let result = fristen.filter((f) => f.status !== 'Erledigt');

    // Textsuche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (f) =>
          f.titel.toLowerCase().includes(term) ||
          (f.adresseName || '').toLowerCase().includes(term) ||
          (f.mandantNummer || '').toLowerCase().includes(term)
      );
    }

    // Filter nach Zeitraum
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + 7);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    switch (filterMode) {
      case 'ueberfaellig':
        result = result.filter((f) => new Date(f.faelligkeitsdatum) < today);
        break;
      case 'heute':
        result = result.filter((f) => {
          const d = new Date(f.faelligkeitsdatum);
          d.setHours(0, 0, 0, 0);
          return d.getTime() === today.getTime();
        });
        break;
      case 'dieseWoche':
        result = result.filter((f) => {
          const d = new Date(f.faelligkeitsdatum);
          return d >= today && d <= endOfWeek;
        });
        break;
      case 'diesesMonat':
        result = result.filter((f) => {
          const d = new Date(f.faelligkeitsdatum);
          return d >= today && d <= endOfMonth;
        });
        break;
    }

    // Kategorie-Filter
    if (kategorieFilter !== 'alle') {
      result = result.filter((f) => f.kategorie === kategorieFilter);
    }

    // Sortieren: Überfällig zuerst, dann nach Datum
    result.sort((a, b) => {
      const dateA = new Date(a.faelligkeitsdatum);
      const dateB = new Date(b.faelligkeitsdatum);
      const isOverdueA = dateA < today;
      const isOverdueB = dateB < today;

      if (isOverdueA && !isOverdueB) return -1;
      if (!isOverdueA && isOverdueB) return 1;
      return dateA.getTime() - dateB.getTime();
    });

    return result;
  }, [fristen, searchTerm, filterMode, kategorieFilter]);

  // Gruppierung nach Kunde
  const fristenByKunde = useMemo(() => {
    const grouped = new Map<number, { name: string; fristen: Frist[] }>();
    filteredFristen.forEach((f) => {
      if (!grouped.has(f.adresseID)) {
        grouped.set(f.adresseID, { name: f.adresseName || 'Unbekannt', fristen: [] });
      }
      grouped.get(f.adresseID)!.fristen.push(f);
    });
    return Array.from(grouped.entries()).sort((a, b) => b[1].fristen.length - a[1].fristen.length);
  }, [filteredFristen]);

  // Gruppierung nach Kategorie
  const fristenByKategorie = useMemo(() => {
    const grouped = new Map<string, Frist[]>();
    filteredFristen.forEach((f) => {
      if (!grouped.has(f.kategorie)) {
        grouped.set(f.kategorie, []);
      }
      grouped.get(f.kategorie)!.push(f);
    });
    return Array.from(grouped.entries()).sort((a, b) => b[1].length - a[1].length);
  }, [filteredFristen]);

  const getFristStatus = (frist: Frist) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fristDate = new Date(frist.faelligkeitsdatum);
    fristDate.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil((fristDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { color: 'error' as const, label: `${Math.abs(diffDays)} Tage überfällig`, icon: <WarningIcon /> };
    if (diffDays === 0) return { color: 'warning' as const, label: 'Heute fällig', icon: <TodayIcon /> };
    if (diffDays <= 7) return { color: 'info' as const, label: `In ${diffDays} Tagen`, icon: <ScheduleIcon /> };
    return { color: 'default' as const, label: `In ${diffDays} Tagen`, icon: <ScheduleIcon /> };
  };

  const renderFristItem = (frist: Frist, showKunde = true) => {
    const status = getFristStatus(frist);

    return (
      <ListItem
        key={frist.fristID}
        sx={{
          bgcolor: status.color === 'error' ? 'error.lighter' : undefined,
          borderRadius: 1,
          mb: 0.5,
        }}
      >
        <ListItemIcon>
          <Tooltip title={status.label}>
            <Box sx={{ color: `${status.color}.main` }}>{status.icon}</Box>
          </Tooltip>
        </ListItemIcon>
        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" fontWeight="medium">
                {frist.titel}
              </Typography>
              <Chip label={frist.kategorie} size="small" variant="outlined" />
              {frist.prioritaet === 'Hoch' && (
                <Chip label="Hoch" size="small" color="error" />
              )}
            </Box>
          }
          secondary={
            <Box display="flex" alignItems="center" gap={2} mt={0.5}>
              {showKunde && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="textSecondary">
                    {frist.adresseName}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color={status.color}>
                {new Date(frist.faelligkeitsdatum).toLocaleDateString('de-CH')} - {status.label}
              </Typography>
            </Box>
          }
        />
        <ListItemSecondaryAction>
          <Box display="flex" gap={0.5}>
            <Tooltip title="Als erledigt markieren">
              <IconButton
                size="small"
                color="success"
                onClick={() => {
                  setSelectedFrist(frist);
                  setErledigtDialogOpen(true);
                }}
              >
                <DoneIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Kunde öffnen">
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedAdresseId(frist.adresseID);
                  setDetailDialogOpen(true);
                }}
              >
                <OpenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Fristen-Dashboard</Typography>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Zeitraum</InputLabel>
            <Select
              value={filterMode}
              label="Zeitraum"
              onChange={(e) => setFilterMode(e.target.value as FilterMode)}
            >
              <MenuItem value="alle">Alle</MenuItem>
              <MenuItem value="ueberfaellig">
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon color="error" fontSize="small" />
                  Überfällig
                </Box>
              </MenuItem>
              <MenuItem value="heute">Heute</MenuItem>
              <MenuItem value="dieseWoche">Diese Woche</MenuItem>
              <MenuItem value="diesesMonat">Diesen Monat</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Kategorie</InputLabel>
            <Select
              value={kategorieFilter}
              label="Kategorie"
              onChange={(e) => setKategorieFilter(e.target.value)}
            >
              <MenuItem value="alle">Alle</MenuItem>
              {kategorien.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Statistik-Karten */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filterMode === 'ueberfaellig' ? 'error.light' : undefined,
            }}
            onClick={() => setFilterMode('ueberfaellig')}
          >
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <WarningIcon color="error" />
                <Box>
                  <Typography variant="h5" color="error">
                    {statistik?.ueberfaellig || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Überfällig
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filterMode === 'heute' ? 'warning.light' : undefined,
            }}
            onClick={() => setFilterMode('heute')}
          >
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <TodayIcon color="warning" />
                <Box>
                  <Typography variant="h5">
                    {fristen.filter(
                      (f) =>
                        f.status !== 'Erledigt' &&
                        new Date(f.faelligkeitsdatum).toDateString() === new Date().toDateString()
                    ).length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Heute
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filterMode === 'dieseWoche' ? 'info.light' : undefined,
            }}
            onClick={() => setFilterMode('dieseWoche')}
          >
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <WeekIcon color="info" />
                <Box>
                  <Typography variant="h5">{statistik?.dieseWoche || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Diese Woche
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card
            sx={{
              cursor: 'pointer',
              bgcolor: filterMode === 'diesesMonat' ? 'primary.light' : undefined,
            }}
            onClick={() => setFilterMode('diesesMonat')}
          >
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <MonthIcon color="primary" />
                <Box>
                  <Typography variant="h5">{statistik?.diesesMonat || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Diesen Monat
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <FristIcon color="action" />
                <Box>
                  <Typography variant="h5">{statistik?.offen || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Offen gesamt
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ py: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircleIcon color="success" />
                <Box>
                  <Typography variant="h5">{statistik?.erledigt || 0}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Erledigt
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Ansichts-Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={viewMode} onChange={(_, v) => setViewMode(v)}>
          <Tab value="liste" label={`Liste (${filteredFristen.length})`} />
          <Tab value="kunde" label={`Nach Kunde (${fristenByKunde.length})`} />
          <Tab value="kategorie" label={`Nach Kategorie (${fristenByKategorie.length})`} />
        </Tabs>
      </Paper>

      {/* Fristen-Liste */}
      <Paper sx={{ p: 2 }}>
        {filteredFristen.length === 0 ? (
          <Box textAlign="center" py={4}>
            <FristIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="textSecondary">
              {filterMode !== 'alle' || kategorieFilter !== 'alle'
                ? 'Keine Fristen entsprechen dem gewählten Filter.'
                : 'Keine offenen Fristen vorhanden.'}
            </Typography>
          </Box>
        ) : viewMode === 'liste' ? (
          <List dense>{filteredFristen.map((f) => renderFristItem(f))}</List>
        ) : viewMode === 'kunde' ? (
          fristenByKunde.map(([adresseId, { name, fristen: kundeFristen }]) => (
            <Box key={adresseId} mb={3}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                mb={1}
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedAdresseId(adresseId);
                  setDetailDialogOpen(true);
                }}
              >
                <BusinessIcon color="primary" />
                <Typography variant="h6">{name}</Typography>
                <Chip label={kundeFristen.length} size="small" color="primary" />
              </Box>
              <List dense>{kundeFristen.map((f) => renderFristItem(f, false))}</List>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))
        ) : (
          fristenByKategorie.map(([kategorie, kategorieFristen]) => (
            <Box key={kategorie} mb={3}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <FristIcon color="action" />
                <Typography variant="h6">{kategorie}</Typography>
                <Chip label={kategorieFristen.length} size="small" />
              </Box>
              <List dense>{kategorieFristen.map((f) => renderFristItem(f))}</List>
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))
        )}
      </Paper>

      {/* Erledigt-Dialog */}
      <Dialog open={erledigtDialogOpen} onClose={() => setErledigtDialogOpen(false)}>
        <DialogTitle>Frist als erledigt markieren?</DialogTitle>
        <DialogContent>
          <Typography>
            Möchten Sie die Frist "{selectedFrist?.titel}" als erledigt markieren?
          </Typography>
          {selectedFrist?.istWiederholend && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Diese Frist ist wiederholend. Es wird automatisch eine neue Frist erstellt.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErledigtDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleMarkAsErledigt} variant="contained" color="success">
            Als erledigt markieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Adresse Detail Dialog */}
      <AdresseDetailDialog
        open={detailDialogOpen}
        adresseId={selectedAdresseId}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedAdresseId(null);
        }}
      />
    </Box>
  );
};

export default FristenDashboard;
